import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["receiptForm", "submit", "currencySelect", "categorySelect", "sum", "sumBaseCurrContainer", "sumBaseCurrInput", 
    "documentTypeSelect", "dateField", "collapsibleContainer", "actionsAtTop", "extraTopPadding",
    "typeDependent", "showIfCardStatement", "showIfOther", "showIfReceipt", "showIfInvoice", "vatRate", 
    "realBookNowButton", "saveChangesBookNowButton", "needsSaving"];

  connect() {
    this.showOrHideDocumentTypeSpecificFields();
    this.showOrHideBaseCurrency();
    this.showOrHideCollapsible();
    this.setInitialActsAsInvoiceState();
    this.checkIfNeedsSaving();
  }

  checkIfNeedsSaving() {
    if (this.needsSavingTarget.value != "false") {
      this.bookingInputsHaveChanged();
    }
  }

  changeDocumentType(event) {
    this.showOrHideDocumentTypeSpecificFields();
    this.showOrHideBaseCurrency();
    this.bookingInputsHaveChanged();
  }

  bookingInputsHaveChanged() {
    this.toggleBookNowButton();
  }

  toggleBookNowButton() {
    if(this.hasRealBookNowButtonTarget) {
      this.realBookNowButtonTarget.classList.add('hidden');
      this.saveChangesBookNowButtonTarget.classList.remove('hidden');
    }
  }

  actsAsInvoice() {
    return this.element.dataset.actsAsInvoiceTypes.includes(this.documentTypeSelectTarget.value)
  }

  setInitialActsAsInvoiceState() {
    this.initialActsAsInvoice = this.actsAsInvoice();
  }

  showOrHideDocumentTypeSpecificFields() {
    this.typeDependentTargets.forEach(el => el.classList.add('hidden'));

    // if we could just get at these helpers we could do this much prettier:
    // https://github.com/hotwired/stimulus/blob/101a060f961e9240b55b29e25fdfe82c79b9c5b0/src/core/string_helpers.ts#L4

    switch(this.documentTypeSelectTarget.value) {
      case 'invoice':
        this.showIfInvoiceTargets.forEach(el => el.classList.remove('hidden'));
        break;
      case 'receipt':
        this.showIfReceiptTargets.forEach(el => el.classList.remove('hidden'));
        break;
      case 'card_statement':
        this.showIfCardStatementTargets.forEach(el => el.classList.remove('hidden'));
        break;
      case 'other':
        this.showIfOtherTargets.forEach(el => el.classList.remove('hidden'));
        break;
    }

    // RTODO:! We may wish to always disable all the hidden form inputs here too (like we currently do for the paid_at field)?
  }

  toggleFormCollapse() {
    this.collapsibleContainerTarget.classList.toggle('hidden')
  }

  showOrHideCollapsible() {
    if(!this.hasActionsAtTopTargetTarget) { return; }
    // If it's a card statement, or we are on mobile, we always show the actions at the top
    if(this.documentTypeIsCardStatement() || window.isMobile) {
      this.actionsAtTopTarget.classList.remove('hidden');
      this.extraTopPaddingTarget.classList.remove('hidden');
      this.extraTopPaddingTarget.classList.add('py-1');
      // Given that we show the option to hide the form...
      // If the server says the form should be collapsed, or we are on mobile, do so
      if(this.element.dataset.collapsedForm == 'true' || window.isMobile) {
        this.collapsibleContainerTarget.classList.add('hidden');
      } else {
        this.collapsibleContainerTarget.classList.remove('hidden');
      }
    } else {
      this.actionsAtTopTarget.classList.add('hidden');
      this.extraTopPaddingTarget.classList.remove('py-1');
      this.extraTopPaddingTarget.classList.add('hidden');
      // If the actions at the top are hidden, we have to show the form
      this.collapsibleContainerTarget.classList.remove('hidden');
    }
  }

  updateFormWithAjax(event, addNewCategory = false, acceptedCategorySuggestion = null, acceptedVatCodeSuggestion = null) {
    document.getElementById('main_flashes').innerHTML = '';
    this.needsSavingTarget.value = true;
    this.receiptFormTarget.dataset.remote = 'true';
    var addNewCategoryParam = addNewCategory ? '?add_new_category=1' : ''
    var categorySuggestionParam = acceptedCategorySuggestion ? `?accept_category_suggestion=${acceptedCategorySuggestion}` : ''
    var vatCodeSuggestionParam = acceptedVatCodeSuggestion ? `?accept_vat_code_suggestion=${acceptedVatCodeSuggestion}` : ''
    this.receiptFormTarget.action = this.receiptFormTarget.dataset.reloadUrl + addNewCategoryParam + categorySuggestionParam + vatCodeSuggestionParam;
    this.receiptFormTarget.method = 'get';
    this.submitTarget.click();
    // Dom extensions need to be re-run after this, but that is handled by receipts/update.js.erb
  }

  checkIfConfirmNeeded(event) {
    if(this.receiptFormTarget.method != 'get' && !this.userHasConfirmed) {
      if(this.element.dataset.hasPaymentMatches == 'true' && this.initialActsAsInvoice != this.actsAsInvoice()) {
        event.preventDefault();
        event.stopPropagation();
        confirm = new Fakturan.Alert.Confirm({ content: 'När du byter mellan leverantörsfaktura och kvitto tas matchningar mot bank bort och behöver göras om. Vill du fortsätta?' }, function(result) {
          if(result == 'true') { this.userHasConfirmed = true; this.submitTarget.click(); }
        }.bind(this));
        confirm.show();
      }
    }
  }

  autofillVat(event) {
    let linkElement = event.target.closest('a');
    var vatRateStr = linkElement.dataset.vatRate;
    this.vatRateTargets.forEach(el => el.value = '');
    var targetField = this.vatRateTargets.find(el => el.dataset.vatRate == vatRateStr);
    var multiplier = (1-1/((100+parseInt(vatRateStr))/100.0));
    var vat_should_be = (parseFloat(this.sumTarget.value) * multiplier).toFixed(2);
    targetField.value = vat_should_be;
    this.updateFormWithAjax(event);
  }

  changeCategory(event) {
    this.updateFormWithAjax(event);
  }

  addNewCategory(event) {
    this.updateFormWithAjax(event, true);
  }

  acceptVatCodeSuggestion(event) {
    event.preventDefault();
    event.stopPropagation();
    this.updateFormWithAjax(event, false, null, event.target.dataset.vatCodeId);
    event.target.remove();
  }

  acceptCategorySuggestion(event) {
    event.preventDefault();
    event.stopPropagation();
    this.updateFormWithAjax(event, false, event.target.dataset.categoryNumber);
    event.target.remove();
  }

  changeCurrency(event) {
    this.showOrHideBaseCurrency();
  }

  // Not currently used
  // documentTypeIsOther() {
  //   return this.documentTypeSelectTarget.value == 'other';
  // }

  documentTypeIsReceipt() {
    return this.documentTypeSelectTarget.value == 'receipt';
  }

  documentTypeIsCardStatement() {
    return this.documentTypeSelectTarget.value == 'card_statement';
  }

  showOrHideBaseCurrency() {
    if(!this.hasSumBaseCurrContainerTarget) { return; }
    if(!this.documentTypeIsReceipt()) { return; }
    this.sumBaseCurrContainerTarget.classList.add('hidden');
    this.sumBaseCurrInputTarget.disabled = true;
    if (this.currencySelectTarget.value != this.currencySelectTarget.form.dataset.baseCurr) {
      this.sumBaseCurrContainerTarget.classList.remove('hidden');
      this.sumBaseCurrInputTarget.disabled = false;
    }
  }
}